import { Card } from "@heroui/react";
import type { ReactNode } from "react";

type DetailSectionProps = {
  title: string;
  /** Compte affiché à côté du titre — `undefined` tant qu'il est inconnu. */
  count?: number;
  /** Ce qui s'affiche quand la section est vide, à la place du contenu. */
  emptyLabel: string;
  children: ReactNode;
  isEmpty: boolean;
};

/**
 * Une section de fiche : un titre, un compte, du contenu ou son absence.
 *
 * ⚠️ **Une section vide reste affichée.** La masquer ferait varier la fiche
 * d'un dossier à l'autre, et « pas d'armée » deviendrait indiscernable de « la
 * section n'existe pas » — un doute que l'administrateur ne peut pas lever.
 *
 * Partagée par la fiche d'un compte et celle d'un événement : c'est ce qui l'a
 * fait remonter de `pages/AccountDetail/` vers `components/`.
 */
export function DetailSection({
  title,
  count,
  emptyLabel,
  isEmpty,
  children,
}: DetailSectionProps) {
  return (
    <Card className="flex flex-col gap-3 p-5">
      <div className="flex flex-wrap items-baseline gap-2">
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          {title}
        </h2>

        {count === undefined ? null : (
          <span className="text-sm text-muted tabular-nums">{count}</span>
        )}
      </div>

      {isEmpty ? (
        <p className="text-sm text-muted italic">{emptyLabel}</p>
      ) : (
        children
      )}
    </Card>
  );
}
